import { MixerHorizontalIcon } from '@radix-ui/react-icons'
import { Table } from '@tanstack/react-table'
import { useTranslation } from '@/hooks/use-translation'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface DataTableViewOptionsProps<TData> {
  table: Table<TData>
  label?: string
  hideOnMobile?: boolean
  width?: string
  columnLabels?: Record<string, string>
}

export function DataTableViewOptions<TData>({
  table,
  label,
  hideOnMobile = true,
  width = '150px',
  columnLabels = {},
}: DataTableViewOptionsProps<TData>) {
  const { t } = useTranslation()

  const buttonClass = hideOnMobile
    ? 'ml-auto hidden h-8 lg:flex'
    : 'ml-auto h-8'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='outline' size='sm' className={buttonClass}>
          <MixerHorizontalIcon className='mr-2 h-4 w-4' />
          {label || t('common.view')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className={`w-[${width}]`}>
        <DropdownMenuLabel>{t('common.toggleColumns')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {table
          .getAllColumns()
          .filter(
            (column) =>
              typeof column.accessorFn !== 'undefined' && column.getCanHide()
          )
          .map((column) => {
            const displayLabel = columnLabels[column.id] || column.id
            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                className='capitalize'
                checked={column.getIsVisible()}
                onCheckedChange={(value) => column.toggleVisibility(!!value)}
              >
                {displayLabel}
              </DropdownMenuCheckboxItem>
            )
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
